/**
 * In-session deletion registry (1.63.x correctness release).
 *
 * When a driver deletes or replaces a photo, an upload for that photo may
 * already be in flight. The durable record is tombstoned and then removed, so
 * by the time the upload finishes there may be nothing left to inspect — and a
 * merely absent record is NOT proof of deletion (a read can fail for other
 * reasons). This registry records the deletion explicitly for the lifetime of
 * the session, which is exactly as long as an in-flight upload can survive.
 *
 * Deliberately tiny so both the delete lifecycle and the upload queue can use
 * it without an import cycle.
 */

import { captureError } from "@/lib/observability";

const cancelledPhotos = new Set<string>();
const cancelledPaths = new Set<string>();

const photoKey = (appraisalId: string, localId: string) => `${appraisalId}:${localId}`;
const normPath = (p: string) => p.trim().replace(/^\/+/, "");

/**
 * Durable path tombstones (replay safety). The in-session sets above and the
 * cleanup queue both disappear after a completed cleanup + app restart, so an
 * older queued UPSERT_PART_INSPECTION snapshot could write a deleted path
 * back. Photo paths are timestamped and never reused, so a deleted path is
 * never legitimately re-linked. Read only by the part-save replay — the
 * upload queue keeps using `isPhotoDeleted` unchanged.
 */
const TOMBSTONE_KEY = "tm_deleted_photo_paths";
const TOMBSTONE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const TOMBSTONE_MAX = 2000;
type Tombstone = { a: string; p: string; t: number };

function readTombstones(): Tombstone[] {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(TOMBSTONE_KEY) : null;
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - TOMBSTONE_TTL_MS;
    return parsed.filter((r): r is Tombstone =>
      !!r && typeof r.a === "string" && typeof r.p === "string" && typeof r.t === "number" && r.t >= cutoff);
  } catch { return []; }
}

function persistTombstones(appraisalId: string, paths: string[]): void {
  if (!appraisalId || paths.length === 0) return;
  try {
    const now = Date.now();
    const records = readTombstones().filter(r => !(r.a === appraisalId && paths.includes(r.p)));
    for (const p of paths) records.push({ a: appraisalId, p, t: now });
    localStorage.setItem(TOMBSTONE_KEY, JSON.stringify(records.slice(-TOMBSTONE_MAX)));
  } catch (error) {
    // Quota/private mode — the in-session registry still applies, so deletion
    // protection is unchanged. Record the failure internally only: no
    // user-facing surface, and telemetry must never break deletion.
    try {
      captureError("appraisal.photo_tombstone_persist", error, {
        severity: "warn",
        metadata: { appraisalId, pathCount: paths.length },
      });
    } catch { /* telemetry must never break photo deletion */ }
  }
}

/** Record that this photo (and any path it was uploading to) was deleted. */
export function markPhotoDeleted(
  appraisalId: string,
  localId: string | null | undefined,
  paths: readonly (string | null | undefined)[] = [],
): void {
  if (localId) cancelledPhotos.add(photoKey(appraisalId, localId));
  const durable: string[] = [];
  for (const path of paths) {
    if (typeof path === "string" && path.trim()) {
      cancelledPaths.add(normPath(path));
      durable.push(normPath(path));
    }
  }
  persistTombstones(appraisalId, durable);
}

/** True when the photo, or the exact object path, was deleted in this session. */
export function isPhotoDeleted(appraisalId: string, localId: string, path?: string | null): boolean {
  if (cancelledPhotos.has(photoKey(appraisalId, localId))) return true;
  if (path && cancelledPaths.has(normPath(path))) return true;
  return false;
}

/** True when this exact path was deliberately deleted on this device (survives restart). */
export function isPathTombstoned(appraisalId: string, path: string | null | undefined): boolean {
  if (!appraisalId || typeof path !== "string" || !path.trim()) return false;
  const p = normPath(path);
  if (cancelledPaths.has(p)) return true;
  return readTombstones().some(r => r.a === appraisalId && r.p === p);
}

/** Test helper — clears the in-session registry (simulates an app restart). */
export function resetPhotoDeletionRegistry(): void {
  cancelledPhotos.clear();
  cancelledPaths.clear();
}

/** Test helper — clears durable tombstones too. */
export function resetPhotoTombstones(): void {
  try { localStorage.removeItem(TOMBSTONE_KEY); } catch { /* noop */ }
}

/**
 * Record a confirmed upload path on the matching draft photo WITHOUT reviving
 * it: a photo deleted while its upload was in flight stays deleted. Before
 * this guard, the upload callback overwrote status "deleted" with "uploaded",
 * which put the deleted photo back into the saved references and the PDF.
 */
export function applyConfirmedUpload<T extends { photoId: string; status: string; storagePath?: string | null; deletedAt?: string }>(
  photos: T[],
  photoId: string,
  path: string,
): T[] {
  return photos.map((p) => {
    if (p.photoId !== photoId) return p;
    if (p.status === "deleted" || p.deletedAt) return p;
    return { ...p, storagePath: path, status: "uploaded" };
  });
}

/** True when a draft photo list shows this photo as deleted (or it is in the registry). */
export function wasDeletedDuringUpload(
  appraisalId: string,
  photos: readonly { photoId: string; status: string; deletedAt?: string }[],
  photoId: string,
  path?: string | null,
): boolean {
  if (isPhotoDeleted(appraisalId, photoId, path)) return true;
  const rec = photos.find((p) => p.photoId === photoId);
  return !!rec && (rec.status === "deleted" || !!rec.deletedAt);
}
